import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Video, Download, RefreshCw, CheckCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VideoHistory from './VideoHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PROMPT_EXAMPLES = [
  "A professional presenter explaining the benefits of AI technology in 30 seconds",
  "A friendly host welcoming new users to our platform with excitement",
  "A doctor explaining healthy habits in a simple and engaging way",
  "A teacher introducing mathematics concepts for students aged 10-12",
];

const ProgressBar = ({ progress, label }) => (
  <div className="w-full space-y-2">
    <div className="flex justify-between text-xs text-gray-500">
      <span>{label}</span>
      <span>{progress}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 rounded-full bg-gradient-to-r from-violet-500 to-purple-600 transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const VideoAgentPage = () => {
  const navigate = useNavigate();
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState('idle'); // idle | generating | polling | done | error
  const [videoUrl, setVideoUrl] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);

  const getToken = () => localStorage.getItem('token');

  const startProgress = () => {
    setProgress(10);
    const steps = [
      { at: 5000,  val: 20 }, { at: 15000, val: 35 }, { at: 30000, val: 50 },
      { at: 60000, val: 65 }, { at: 90000, val: 78 }, { at: 120000, val: 88 },
      { at: 150000, val: 93 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(v => Math.max(v, s.val)), s.at));
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error('Please describe your video'); return; }
    const token = getToken();
    if (!token) { toast.error('Please sign in first'); return; }
    setStatus('generating'); setStatusMsg('Submitting your prompt...'); setProgress(5);
    try {
      const res = await axios.post(`${API}/heygen/video-agent`, { prompt }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.success && res.data.video_id) {
        setStatus('polling'); setStatusMsg('Video queued — AI is working on it...');
        startProgress();
        pollVideo(res.data.video_id, res.data.record_id);
      }
    } catch (e) {
      setStatus('error');
      setStatusMsg(e.response?.data?.detail || 'Generation failed. Please try again.');
    }
  };

  const pollVideo = (id, recId) => {
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts++;
      if (attempts > 120) {
        clearInterval(interval); setStatus('error');
        setStatusMsg('Timed out. Please try again.'); return;
      }
      try {
        const res = await axios.get(`${API}/heygen/video-status/${id}`);
        const { status: s, video_url, thumbnail_url } = res.data;
        if (s === 'completed' && video_url) {
          clearInterval(interval);
          setProgress(100); setVideoUrl(video_url); setStatus('done');
          toast.success('Your video is ready!');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'completed', video_url, thumbnail_url },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else if (s === 'failed') {
          clearInterval(interval); setStatus('error'); setStatusMsg('Video generation failed. Try again.');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'failed' },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else {
          const pct = Math.min(93, 10 + attempts * 1.5);
          setStatusMsg(`Processing your video... (${s})`);
          setProgress(v => Math.max(v, Math.floor(pct)));
        }
      } catch (_) {}
    }, 5000);
  };

  const reset = () => { setPrompt(''); setStatus('idle'); setVideoUrl(null); setStatusMsg(''); setProgress(0); };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = videoUrl;
    a.download = 'ai-video.mp4';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-purple-50">
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <button onClick={() => navigate('/creator-ai/heygen-hub')} className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-2 text-xs sm:text-sm">
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-4 w-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Video Agent</h1>
              <p className="text-xs text-gray-500">Type a prompt → AI creates a full video · 200 credits</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 sm:py-8 space-y-4">

        {/* IDLE */}
        {status === 'idle' && (
          <>
            <VideoHistory videoType="video_agent" accentColor="violet" />
            <div className="text-center">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">Describe Your Video</h2>
              <p className="text-gray-500 text-xs sm:text-sm">No avatar setup needed. Just describe what you want.</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-3">
              <label className="block text-sm font-semibold text-gray-700">Your Video Prompt</label>
              <textarea
                value={prompt} onChange={e => setPrompt(e.target.value)} rows={4}
                placeholder="Example: A professional presenter explaining the benefits of AI in healthcare..."
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-400 resize-none"
              />
              <p className="text-xs text-gray-400">💡 Be specific about topic, tone and audience for best results.</p>
              <button onClick={generate} disabled={!prompt.trim()}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold py-3.5 rounded-xl hover:opacity-90 active:opacity-80 disabled:opacity-40 flex items-center justify-center gap-2 text-sm">
                <Sparkles className="h-4 w-4" /> Generate Video (200 credits)
              </button>
            </div>
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">💡 Example Prompts — tap to use</p>
              <div className="space-y-2">
                {PROMPT_EXAMPLES.map((ex, i) => (
                  <button key={i} onClick={() => setPrompt(ex)}
                    className="w-full text-left text-xs sm:text-sm px-3 py-2.5 bg-violet-50 active:bg-violet-100 hover:bg-violet-100 text-violet-800 rounded-xl border border-violet-200 transition-colors">
                    {ex}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {/* GENERATING / POLLING */}
        {(status === 'generating' || status === 'polling') && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                <Video className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Creating Your Video</p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">{statusMsg}</p>
              </div>
            </div>

            <ProgressBar progress={progress} label="Generating..." />

            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-800">⏱ Average time: <strong>1–3 minutes</strong>. Please keep this page open.</p>
            </div>
          </div>
        )}

        {/* ERROR */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-6 text-center space-y-4">
            <p className="text-red-600 font-medium text-sm">{statusMsg}</p>
            <button onClick={reset} className="bg-gray-900 text-white px-6 py-3 rounded-xl font-medium flex items-center gap-2 mx-auto text-sm">
              <RefreshCw className="h-4 w-4" /> Try Again
            </button>
          </div>
        )}

        {/* DONE */}
        {status === 'done' && videoUrl && (
          <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600 mb-1">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold text-base">Your Video is Ready!</span>
            </div>

            {/* Video Player */}
            <div className="bg-black rounded-xl overflow-hidden">
              <video
                src={videoUrl}
                controls
                muted
                playsInline
                className="w-full max-h-[300px] sm:max-h-[420px]"
                onError={() => toast.error('Preview failed — use the Open button below to view your video')}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:opacity-90 text-sm"
              >
                <Download className="h-4 w-4" /> Download Video
              </button>
              <a
                href={videoUrl} target="_blank" rel="noreferrer"
                className="w-full border-2 border-violet-300 text-violet-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-violet-50 text-sm"
              >
                <ExternalLink className="h-4 w-4" /> Open in Browser
              </a>
              <button
                onClick={reset}
                className="w-full border-2 border-gray-200 text-gray-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm"
              >
                <RefreshCw className="h-4 w-4" /> Create Another
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default VideoAgentPage;
